import { BoxProps, PlacementWithLogical } from '@chakra-ui/react';
import React, { FocusEvent, ReactNode } from 'react';

type BrandColor = {
  color: string;
  label: string;
};
type Color = string;
type ColorSwatch<T = Color> = T[];
type OnChange = (color: Color) => void;
type OnSetSwatchColor = (color: Color) => void;
type OnRemoveSwatchColor = (color: Color) => void;
type IconStyle = 'fill' | 'border' | 'text' | 'step';

interface DefaultTriggerProps {
  onClick: () => void;
  onBlur: () => void;
  onFocus: () => void;
  label: string | undefined;
  iconStyle: IconStyle;
  color: string;
  iconColors: string[];
  triggerButtonRef: React.RefObject<HTMLButtonElement> | null;
}

interface ColorPickerProps {
  children?: ReactNode;
  color: Color;
  // The tooltip label - for a colorpicker without tooltip don't pass this prop
  label?: string;
  showBrandSection?: boolean;
  onChange: OnChange;

  // render auto brand modal and a new button
  brandComponent?: ReactNode;

  // onBlur event listener for the color picker container
  onContainerBlur?: (e: FocusEvent, isOpen: boolean) => void;

  showNoColorOption?: boolean;
  showNoColorChecker?: boolean;
  showOpacityOption?: boolean;
  iconStyle?: IconStyle;
  // For displaying multiple colors in the trigger button
  iconColors?: Color[];
  placement?: PlacementWithLogical;
  colorSwatch: ColorSwatch;
  onSetSwatchColor: OnSetSwatchColor;
  onRemoveSwatchColor: OnRemoveSwatchColor;
  colorCheckerProps?: SingleColorContrastCheckerProps;
  hasArrow?: boolean;
  onOpen?: () => void;
  onClose?: () => void;

  // Border to apply to the saturation picker
  saturationBorderColor?: string;

  // Brand Kit Section
  brandColors?: BrandColor[];
  brandSwitchMenu?: ReactNode;
  brandUpgrade?: ReactNode;
  hasBrandPermission?: boolean;

  // trigger button styling section
  h?: BoxProps['h'];
  w?: BoxProps['w'];

  // components ids to be skipped when clicking outside the component
  skipOutsideOnCloseIds?: string[];
}

type ColorSwatchProps = {
  showNoColorOption?: boolean;
  color: Color;
  onChange: OnChange;
  colorSwatch: ColorSwatch;
  onSetSwatchColor: () => void;
  onRemoveSwatchColor: OnRemoveSwatchColor;

  // Add color button swatch ref, currently used for navigate backward using keyboard tab
  addButtonRef: React.RefObject<HTMLButtonElement> | null;

  // Number of columns to display in the swatch
  numCols?: number;
};

interface BrandColorProps {
  brandColors: BrandColor[];
  brandSwitchMenu?: ReactNode;
  brandUpgrade?: ReactNode;
  brandComponent?: ReactNode;
  color?: Color;
  hasBrandPermission: boolean;
  onChange: OnChange;
  numCols?: number;
}

interface SwatchListProps {
  colorSwatch: ColorSwatch;
  onChange: OnChange;
  resetFocus: (idx: number) => void;
  onRemoveSwatchColor: OnRemoveSwatchColor;
  className?: string;
}

interface BrandColorListProps {
  colorSwatch: ColorSwatch<BrandColor>;
  isDisabled?: boolean;
  onChange: OnChange;
  onFocus?: () => void;
}

interface SwatchButtonProps {
  onClick: () => void;
  label: string;
  color: Color;
  testId?: string;
  swatchRef?: React.RefObject<HTMLButtonElement> | null;
  icon?: React.ReactElement<any, string | React.JSXElementConstructor<any>> | undefined;
  onRemove?: () => void;
  onFocus?: () => void;
  isDisabled?: boolean;
  id?: string;
  border?: string;
  className?: string;
}

interface RGBAParsed {
  r: number;
  g: number;
  b: number;
  a: number;
}

interface CheckeredOverlayProps {
  color: Color;
  parsedColor: RGBAParsed;
}

interface SingleColorContrastCheckerProps {
  /**
   * Current selected text color to validate
   */
  color: Color;

  /**
   * Current selected target area
   */
  targetRect?: DOMRect;

  /**
   * Triggered when the contrast check status is updated
   * i.e. score change, score is calculating
   */
  onUpdate?: (result: { isAA: boolean; isAAA: boolean; hasScore: boolean }) => void;

  /**
   * Selected text style within target area
   */
  textStyle?: {
    fontSize: string;
    fontFamily: string;
    fontWeight: string;
  };
}

interface ColorPickerContentContainerProps {
  containerId: string | string[];
  children: ReactNode;
  closePopover: () => void;
}

export enum ContrastCheckStatus {
  Fail = 'Fail',
  Pass = 'Pass',
  Disabled = 'Disabled',
}

export type {
  BrandColorProps,
  BrandColorListProps,
  RGBAParsed,
  Color,
  ColorPickerProps,
  ColorSwatchProps,
  SwatchButtonProps,
  SwatchListProps,
  CheckeredOverlayProps,
  SingleColorContrastCheckerProps,
  IconStyle,
  DefaultTriggerProps,
  ColorPickerContentContainerProps,
  OnChange,
  BrandColor,
};
